import React, { useState } from "react";
import { Modal, Form, message } from "antd";
import { addAdmin } from "../../../services/AdminPortalAPIs";
import { roles } from "./SettingsColumn";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledPasswordInput from "src/components/reusable/StyledPasswordInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";

interface AddAdminModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddAdminModal: React.FC<AddAdminModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await addAdmin(values);
      message.success("Admin added successfully");
      onSuccess();
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      message.error("Failed to add admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Add New Admin"
      okText="Add"
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="user_name" label="Name" rules={[{ required: true }]}>
          <StyledTextInput aria-label="Admin Name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email address" },
          ]}
        >
          <StyledTextInput aria-label="Admin Email" />
        </Form.Item>

        {/* Updated field: role_name dropdown */}
        <Form.Item
          name="role_name"
          label="Role"
          rules={[{ required: true, message: "Please select a role" }]}
        >
         <StyledDropdown
          placeholder="Select role"
          aria-label="Admin Role"
          options={roles.map(role => ({ value: role, label: role }))}
          popupClassName="custom-dropdown-menu"
         />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: "Password is required" },
            {
              validator: (_, value) =>
                value && value.length >= 8
                  ? Promise.resolve()
                  : Promise.reject("Password must be at least 8 characters"),
            },
          ]}
        >
          <StyledPasswordInput  aria-label="Password"/>
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label="Confirm Password"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                return value === getFieldValue("password")
                  ? Promise.resolve()
                  : Promise.reject("Passwords do not match");
              },
            }),
          ]}
        >
          <StyledPasswordInput autoComplete="new-password" aria-label="Confirm Password"/>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddAdminModal;
